import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  Button,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import * as services from "../../../services/saleng";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";

export default function MonthlyWithdrawal() {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const startDate = `${selectedMonth}-01`;

  const endDate = new Date(
    Number(selectedMonth.split("-")[0]),
    Number(selectedMonth.split("-")[1]),
    0,
  )
    .toISOString()
    .split("T")[0];

  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);

  const cellStyle = {
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await services.getBatches();

      const data = res?.data ?? [];

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNum = (val) =>
    Number(val || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatThaiDate = (dateStr) => {
    if (!dateStr) return "-";

    const d = new Date(dateStr);

    return d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ---------------- DATASET ----------------
  const dataset = useMemo(() => {
    const data = [];

    rows.forEach((batch) => {
      if (!batch.isPickedUp) return;

      const pickedDate = new Date(batch.pickedUpAt);

      const pickedMonth = `${pickedDate.getFullYear()}-${String(
        pickedDate.getMonth() + 1,
      ).padStart(2, "0")}`;

      if (pickedMonth !== selectedMonth) return;

      const productNames = Array.from(
        new Set(
          batch.transactions
            ?.flatMap((tx) => tx.items?.map((item) => item.productName))
            .filter(Boolean),
        ),
      );

      data.push({
        date: batch.pickedUpAt,

        productName: productNames.join(", ") || "-",

        price: 0,

        unit: "กก.",

        qty: Number(batch.totalWeight || 0),

        total: 0,

        batch: batch.batchNumber || "-", // <- หมายเหตุ
      });
    });

    data.sort((a, b) => new Date(a.date) - new Date(b.date));

    return data;
  }, [rows, selectedMonth]);

  // ---------------- SUMMARY ----------------
  const summary = useMemo(() => {
    return dataset.reduce(
      (acc, item) => {
        acc.qty += item.qty;
        acc.total += item.total;

        return acc;
      },
      {
        qty: 0,
        total: 0,
      },
    );
  }, [dataset]);

  // ---------------- EXPORT CSV ----------------
  const exportToCSV = () => {
    if (!dataset.length) return;

    const headers = [
      "วันที่เบิก",
      "Batch รับเข้า",
      "Batch เบิก",
      "ชื่อสินค้า",
      "น้ำหนัก",
      "จำนวนรายการ",
    ];

    const rowsCSV = dataset.map((item) => [
      formatThaiDate(item.pickedUpAt),
      item.batchNumber,
      item.pickedUpCode,
      item.productName,
      item.totalWeight.toFixed(2),
      item.transactionCount,
    ]);

    const csvContent = [
      headers.join(","),
      ...rowsCSV.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);

    link.download = `monthly_withdrawal_${selectedMonth}.csv`;

    link.click();
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs />

      {/* FILTER */}
      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <TextField
          label="เดือน"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={exportToCSV}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            รายงานการเบิกสินค้า
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              {/* HEADER */}
              <TableHead
                sx={{
                  backgroundColor: "#f0f0f0",
                }}
              >
                <TableRow>
                  <TableCell sx={cellStyle}>วันที่</TableCell>

                  <TableCell sx={cellStyle}>ชื่อสินค้า</TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    ราคา
                  </TableCell>

                  <TableCell sx={cellStyle}>หน่วยนับ</TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    รวม
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    มูลค่ารวม
                  </TableCell>

                  <TableCell sx={cellStyle}>หมายเหตุ</TableCell>
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>
                {dataset.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={cellStyle}>
                      {formatThaiDate(item.date)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.productName}</TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.price)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.unit}</TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.qty)}
                    </TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.total)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.batch}</TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {/* FOOTER */}
              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": {
                      fontWeight: "bold",
                    },
                  }}
                >
                  <TableCell sx={cellStyle} colSpan={4}>
                    รวมทั้งหมด
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.weight)}
                  </TableCell>

                  <TableCell sx={cellStyle} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
